import { useEffect, useMemo, useState } from "react";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import { Button, Tooltip } from "@mui/material";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import {
  MESSENGER_IDS,
  MESSENGERS,
  getAccessModeHintForMessengers,
  getGroupShareLink,
  getProjectShareLinks,
  isMessengerId,
  normalizeAvailableMessengers,
} from "../utils/messengers";
import { MaxBrandIcon, TelegramBrandIcon } from "./MessengerBrandIcons";
import { getHrShareInstruction } from "../utils/hrShareGuide";

const MESSENGER_ICONS = {
  telegram: TelegramBrandIcon,
  max: MaxBrandIcon,
};

export function readAvailableMessengersFromSettings(gameSettings) {
  return normalizeAvailableMessengers(gameSettings ?? {});
}

export default function ProjectMessengerAvailability({
  gameId,
  value,
  onChange,
  readOnly = false,
  compact = false,
  showShareLinks = true,
  hintExtra,
}) {
  const [copiedId, setCopiedId] = useState(null);
  const [maxBotUsername, setMaxBotUsername] = useState("");

  useEffect(() => {
    const baseUrl = (process.env.REACT_APP_SERVER_URL || window.location.origin)
      .replace(/\/$/, "");

    fetch(`${baseUrl}/api/max/bot-info`)
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        if (data?.username) {
          setMaxBotUsername(String(data.username).replace(/^@/, ""));
        }
      })
      .catch(() => {});
  }, []);

  const availableMessengers = useMemo(() => {
    const normalized = (value ?? []).filter(isMessengerId);
    return normalized.length > 0 ? normalized : ["telegram"];
  }, [value]);

  const shareLinks = useMemo(() => {
    if (!gameId || !showShareLinks) return [];
    const links = getProjectShareLinks(gameId, availableMessengers);
    if (!maxBotUsername) {
      return links;
    }
    return links.map((item) =>
      item.id === "max"
        ? { ...item, link: getGroupShareLink(gameId, "max", maxBotUsername) }
        : item,
    );
  }, [gameId, availableMessengers, showShareLinks, maxBotUsername]);

  const accessHint = getAccessModeHintForMessengers(availableMessengers);
  const hrInstruction = getHrShareInstruction(availableMessengers);

  const handleToggle = (messengerId) => (event) => {
    if (readOnly || !onChange) return;

    const checked = event.target.checked;
    let next = checked
      ? [...availableMessengers, messengerId]
      : availableMessengers.filter((id) => id !== messengerId);

    next = MESSENGER_IDS.filter((id) => next.includes(id));

    if (next.length === 0) {
      return;
    }

    onChange(next);
  };

  const handleCopy = async (text, copyId) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(copyId);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error("Failed to copy text: ", err);
    }
  };

  return (
    <div
      className={`app-project-messenger${
        compact ? " app-project-messenger--compact" : ""
      }${readOnly ? " app-project-messenger--readonly" : ""}`}
    >
      <div className="app-project-messenger__pick">
        <span className="app-settings-field__label">
          {readOnly ? "Доступно в мессенджерах" : "Доступность в мессенджерах"}
        </span>
        <FormGroup row className="app-project-messenger__options">
          {MESSENGER_IDS.map((id) => {
            const meta = MESSENGERS[id];
            const BrandIcon = MESSENGER_ICONS[id];
            const checked = availableMessengers.includes(id);
            const isLastSelected = checked && availableMessengers.length === 1;

            return (
              <FormControlLabel
                key={id}
                className="app-project-messenger__option"
                control={
                  <Checkbox
                    size="small"
                    checked={checked}
                    disabled={readOnly || isLastSelected}
                    onChange={handleToggle(id)}
                  />
                }
                label={
                  <span className="app-project-messenger__option-label">
                    <BrandIcon
                      className="app-project-messenger__option-icon"
                      size={compact ? 18 : 20}
                    />
                    {meta.label}
                  </span>
                }
              />
            );
          })}
        </FormGroup>
        {!readOnly ? (
          <p className="app-project-messenger__pick-hint">
            Можно выбрать один мессенджер или оба — для каждого будет своя ссылка.
          </p>
        ) : null}
      </div>

      <p
        className={`app-access-segment__hint app-access-segment__hint--team${
          compact ? " app-access-segment__hint--compact" : ""
        }`}
      >
        {accessHint}
        {hintExtra}
      </p>

      {showShareLinks && shareLinks.length > 0 ? (
        <div className="app-project-messenger__links">
          <div className="app-hr-share-guide">
            <strong className="app-hr-share-guide__title">Как раздать ссылку HR</strong>
            <p className="app-hr-share-guide__text">{hrInstruction}</p>
          </div>
          {shareLinks.map((item) => (
            <div key={item.id} className="app-settings-share-block">
              <span className="app-settings-share-label">
                Ссылка для {item.label}
              </span>
              <div className="app-editor-access__link-row app-editor-access__link-row--compact">
                <LinkOutlinedIcon className="app-editor-access__link-icon" />
                <span className="app-editor-access__link">{item.link}</span>
                <Tooltip
                  title={
                    copiedId === item.id ? "Скопировано" : "Копировать ссылку"
                  }
                  placement="top"
                >
                  <Button
                    size="small"
                    variant="outlined"
                    className="app-btn app-btn--secondary app-btn--compact"
                    onClick={() => handleCopy(item.link, item.id)}
                  >
                    Копировать
                  </Button>
                </Tooltip>
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
